import { MapPinIcon } from '@heroicons/react/24/outline';
import { Category } from '../types/category';
import { ActivityType } from '../types/activityType';

interface FindActivitiesFormProps {
  categories: Category[];
  activityTypes: ActivityType[];
  selectedCategory: string;
  selectedType: string;
  selectedChild: string;
  age: string;
  location: string;
  distance: string;
  condition: string;
  duration: string;
  considerations: string;
  maxCost: string;
  isLoading: boolean;
  onCategoryChange: (value: string) => void;
  onTypeChange: (value: string) => void;
  onChildChange: (value: string) => void;
  onAgeChange: (value: string) => void;
  onLocationChange: (value: string) => void;
  onDistanceChange: (value: string) => void;
  onConditionChange: (value: string) => void;
  onDurationChange: (value: string) => void;
  onConsiderationsChange: (value: string) => void;
  onCurrentLocation: () => void;
  onSearch: (searchParams: any) => void;
  onClear: () => void;
  showClearButton: boolean;
  onMaxCostChange: (value: string) => void;
}

const DISTANCE_OPTIONS = [
  { value: '5', label: '5 miles' },
  { value: '10', label: '10 miles' },
  { value: '15', label: '15 miles' },
  { value: '25', label: '25 miles' },
  { value: '50', label: '50 miles' },
  { value: '100', label: '100 miles' }
];

const CONDITIONS = [
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'no-preference', label: 'No Preference' }
];

const FindActivitiesForm = ({
  categories = [],
  activityTypes = [],
  selectedCategory = '',
  selectedType = '',
  selectedChild = 'n/a',
  age = '',
  location = '',
  distance = '',
  condition = '',
  duration = '',
  considerations = '',
  maxCost = '',
  isLoading = false,
  onCategoryChange,
  onTypeChange,
  onChildChange,
  onAgeChange,
  onLocationChange,
  onDistanceChange,
  onConditionChange,
  onDurationChange,
  onConsiderationsChange,
  onCurrentLocation,
  onSearch,
  onClear,
  showClearButton = false,
  onMaxCostChange
}: FindActivitiesFormProps) => {
  console.log('FindActivitiesForm Props:', {
    categories,
    activityTypes,
    selectedCategory,
    selectedType,
    selectedChild,
    age,
    location,
    distance,
    condition,
    duration,
    considerations,
    maxCost,
    isLoading,
    showClearButton
  });

  console.log('Selected Category:', selectedCategory);
  console.log('Selected Type:', selectedType);
  console.log('Selected Child:', selectedChild);
  console.log('Age:', age);
  console.log('Location:', location);
  console.log('Distance:', distance);
  console.log('Condition:', condition);
  console.log('Duration:', duration);
  console.log('Considerations:', considerations);
  console.log('Max Cost:', maxCost);

  if (!categories || !Array.isArray(categories) || categories.length === 0) {
    console.warn('No categories available');
    return <div>No categories available.</div>;
  }

  if (!activityTypes || !Array.isArray(activityTypes) || activityTypes.length === 0) {
    console.warn('No activity types available');
    return <div>No activity types available.</div>;
  }

  if (!selectedCategory || typeof selectedCategory !== 'string') {
    console.warn('Invalid selected category');
    return <div>Invalid selected category.</div>;
  }

  if (!selectedType || typeof selectedType !== 'string') {
    console.warn('Invalid selected type');
    return <div>Invalid selected type.</div>;
  }

  if (!selectedChild || typeof selectedChild !== 'string') {
    console.warn('Invalid selected child');
    return <div>Invalid selected child.</div>;
  }

  if (typeof age !== 'string' || (selectedChild === 'n/a' && !age.trim())) {
    console.warn('Invalid age');
    return (
      <div>
        <p>Please enter a valid age.</p>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => onCategoryChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Categories</option>
                {categories
                  .filter(cat => cat.type === 'standard')
                  .map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Activity Type
              </label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => onTypeChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Types</option>
                {activityTypes.map(type => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="child" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Child
              </label>
              <select
                id="child"
                value={selectedChild}
                onChange={(e) => onChildChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Children</option>
                {categories.filter(cat => cat.type === 'children').map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>

            {(selectedChild === 'n/a' || categories.filter(cat => cat.type === 'children').length === 0) && (
              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Age
                </label>
                <input
                  type="text"
                  value={age}
                  onChange={(e) => onAgeChange(e.target.value)}
                  placeholder="Enter age"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            )}

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Duration (minutes)
              </label>
              <input
                type="number"
                id="duration"
                value={duration}
                onChange={(e) => onDurationChange(e.target.value)}
                min="15"
                max="240"
                step="15"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label htmlFor="condition" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Environment
              </label>
              <select
                id="condition"
                value={condition}
                onChange={(e) => onConditionChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {CONDITIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Location
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => onLocationChange(e.target.value)}
                  placeholder="Enter a location"
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 pr-10 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
                <button
                  type="button"
                  onClick={onCurrentLocation}
                  className="absolute inset-y-0 right-0 px-3 flex items-center"
                >
                  <MapPinIcon className="h-5 w-5 text-gray-400 hover:text-blue-500" />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="distance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Distance
              </label>
              <select
                id="distance"
                value={distance}
                onChange={(e) => onDistanceChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {DISTANCE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="maxCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Max Cost
              </label>
              <input
                type="number"
                id="maxCost"
                value={maxCost}
                onChange={(e) => onMaxCostChange(e.target.value)}
                min="0"
                max="1000"
                step="10"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="considerations" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Special Considerations
              </label>
              <textarea
                id="considerations"
                value={considerations}
                onChange={(e) => onConsiderationsChange(e.target.value)}
                placeholder="Any special needs, preferences, or requirements?"
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => handleSearch()}
              disabled={isLoading}
              className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Searching...' : 'Find Activities'}
            </button>

            {showClearButton && (
              <button
                onClick={onClear}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  console.log('Location before validation:', location);

  if (!location || typeof location !== 'string') {
    console.warn('Invalid location');
    return (
      <div>
        <p>Please enter a valid location.</p>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => onCategoryChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Categories</option>
                {categories
                  .filter(cat => cat.type === 'standard')
                  .map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Activity Type
              </label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => onTypeChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Types</option>
                {activityTypes.map(type => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="child" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Child
              </label>
              <select
                id="child"
                value={selectedChild}
                onChange={(e) => onChildChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Children</option>
                {categories.filter(cat => cat.type === 'children').map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>

            {(selectedChild === 'n/a' || categories.filter(cat => cat.type === 'children').length === 0) && (
              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Age
                </label>
                <input
                  type="text"
                  value={age}
                  onChange={(e) => onAgeChange(e.target.value)}
                  placeholder="Enter age"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            )}

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Duration (minutes)
              </label>
              <input
                type="number"
                id="duration"
                value={duration}
                onChange={(e) => onDurationChange(e.target.value)}
                min="15"
                max="240"
                step="15"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label htmlFor="condition" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Environment
              </label>
              <select
                id="condition"
                value={condition}
                onChange={(e) => onConditionChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {CONDITIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Location
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => onLocationChange(e.target.value)}
                  placeholder="Enter a location"
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 pr-10 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
                <button
                  type="button"
                  onClick={onCurrentLocation}
                  className="absolute inset-y-0 right-0 px-3 flex items-center"
                >
                  <MapPinIcon className="h-5 w-5 text-gray-400 hover:text-blue-500" />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="distance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Distance
              </label>
              <select
                id="distance"
                value={distance}
                onChange={(e) => onDistanceChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {DISTANCE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="maxCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Max Cost
              </label>
              <input
                type="number"
                id="maxCost"
                value={maxCost}
                onChange={(e) => onMaxCostChange(e.target.value)}
                min="0"
                max="1000"
                step="10"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="considerations" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Special Considerations
              </label>
              <textarea
                id="considerations"
                value={considerations}
                onChange={(e) => onConsiderationsChange(e.target.value)}
                placeholder="Any special needs, preferences, or requirements?"
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => handleSearch()}
              disabled={isLoading}
              className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Searching...' : 'Find Activities'}
            </button>

            {showClearButton && (
              <button
                onClick={onClear}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  console.log('Considerations before validation:', considerations);

  if (typeof considerations !== 'string') {
    console.warn('Invalid considerations');
    return (
      <div>
        <p>Please enter valid considerations.</p>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Category
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => onCategoryChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Categories</option>
                {categories
                  .filter(cat => cat.type === 'standard')
                  .map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))
                }
              </select>
            </div>

            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Activity Type
              </label>
              <select
                id="type"
                value={selectedType}
                onChange={(e) => onTypeChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Types</option>
                {activityTypes.map(type => (
                  <option key={type.id} value={type.name}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="child" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Child
              </label>
              <select
                id="child"
                value={selectedChild}
                onChange={(e) => onChildChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Children</option>
                {categories.filter(cat => cat.type === 'children').map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>

            {(selectedChild === 'n/a' || categories.filter(cat => cat.type === 'children').length === 0) && (
              <div>
                <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Age
                </label>
                <input
                  type="text"
                  value={age}
                  onChange={(e) => onAgeChange(e.target.value)}
                  placeholder="Enter age"
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            )}

            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Duration (minutes)
              </label>
              <input
                type="number"
                id="duration"
                value={duration}
                onChange={(e) => onDurationChange(e.target.value)}
                min="15"
                max="240"
                step="15"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div>
              <label htmlFor="condition" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Environment
              </label>
              <select
                id="condition"
                value={condition}
                onChange={(e) => onConditionChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {CONDITIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Location
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => onLocationChange(e.target.value)}
                  placeholder="Enter a location"
                  className="block w-full rounded-md border-gray-300 dark:border-gray-600 pr-10 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                />
                <button
                  type="button"
                  onClick={onCurrentLocation}
                  className="absolute inset-y-0 right-0 px-3 flex items-center"
                >
                  <MapPinIcon className="h-5 w-5 text-gray-400 hover:text-blue-500" />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="distance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Distance
              </label>
              <select
                id="distance"
                value={distance}
                onChange={(e) => onDistanceChange(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                {DISTANCE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="maxCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Max Cost
              </label>
              <input
                type="number"
                id="maxCost"
                value={maxCost}
                onChange={(e) => onMaxCostChange(e.target.value)}
                min="0"
                max="1000"
                step="10"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="considerations" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Special Considerations
              </label>
              <textarea
                id="considerations"
                value={considerations}
                onChange={(e) => onConsiderationsChange(e.target.value)}
                placeholder="Any special needs, preferences, or requirements?"
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              onClick={() => handleSearch()}
              disabled={isLoading}
              className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {isLoading ? 'Searching...' : 'Find Activities'}
            </button>

            {showClearButton && (
              <button
                onClick={onClear}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!distance || typeof distance !== 'string') {
    console.warn('Invalid distance');
    return <div>Invalid distance.</div>;
  }

  if (!condition || typeof condition !== 'string') {
    console.warn('Invalid condition');
    return <div>Invalid condition.</div>;
  }

  if (!duration || typeof duration !== 'string') {
    console.warn('Invalid duration');
    return <div>Invalid duration.</div>;
  }

  if (!maxCost || typeof maxCost !== 'string') {
    console.warn('Invalid maxCost');
    return <div>Invalid maxCost.</div>;
  }

  if (typeof isLoading !== 'boolean') {
    console.warn('Invalid isLoading');
    return <div>Invalid isLoading.</div>;
  }

  if (typeof onCategoryChange !== 'function') {
    console.warn('Invalid onCategoryChange');
    return <div>Invalid onCategoryChange.</div>;
  }

  if (typeof onTypeChange !== 'function') {
    console.warn('Invalid onTypeChange');
    return <div>Invalid onTypeChange.</div>;
  }

  if (typeof onChildChange !== 'function') {
    console.warn('Invalid onChildChange');
    return <div>Invalid onChildChange.</div>;
  }

  if (typeof onAgeChange !== 'function') {
    console.warn('Invalid onAgeChange');
    return <div>Invalid onAgeChange.</div>;
  }

  if (typeof onLocationChange !== 'function') {
    console.warn('Invalid onLocationChange');
    return <div>Invalid onLocationChange.</div>;
  }

  if (typeof onDistanceChange !== 'function') {
    console.warn('Invalid onDistanceChange');
    return <div>Invalid onDistanceChange.</div>;
  }

  if (typeof onConditionChange !== 'function') {
    console.warn('Invalid onConditionChange');
    return <div>Invalid onConditionChange.</div>;
  }

  if (typeof onDurationChange !== 'function') {
    console.warn('Invalid onDurationChange');
    return <div>Invalid onDurationChange.</div>;
  }

  if (typeof onConsiderationsChange !== 'function') {
    console.warn('Invalid onConsiderationsChange');
    return <div>Invalid onConsiderationsChange.</div>;
  }

  if (typeof onCurrentLocation !== 'function') {
    console.warn('Invalid onCurrentLocation');
    return <div>Invalid onCurrentLocation.</div>;
  }

  if (typeof onSearch !== 'function') {
    console.warn('Invalid onSearch');
    return <div>Invalid onSearch.</div>;
  }

  if (typeof onClear !== 'function') {
    console.warn('Invalid onClear');
    return <div>Invalid onClear.</div>;
  }

  if (typeof showClearButton !== 'boolean') {
    console.warn('Invalid showClearButton');
    return <div>Invalid showClearButton.</div>;
  }

  if (typeof onMaxCostChange !== 'function') {
    console.warn('Invalid onMaxCostChange');
    return <div>Invalid onMaxCostChange.</div>;
  }

  const handleSearch = () => {
    console.log('Search button clicked');
    console.log('Search Params:', {
      category: selectedCategory,
      type: selectedType,
      child: selectedChild,
      age,
      location,
      distance,
      condition,
      duration,
      maxCost,
      considerations: considerations.trim() ? considerations : undefined
    });

    const searchParams = {
      category: selectedCategory,
      type: selectedType,
      child: selectedChild,
      age,
      location,
      distance,
      condition,
      duration,
      maxCost,
      considerations: considerations.trim() ? considerations : undefined
    };

    onSearch(searchParams);
  };

  const childrenCategories = categories.filter(cat => cat.type === 'children');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Category
          </label>
          <select
            id="category"
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Categories</option>
            {categories
              .filter(cat => cat.type === 'standard')
              .map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))
            }
          </select>
        </div>

        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Activity Type
          </label>
          <select
            id="type"
            value={selectedType}
            onChange={(e) => onTypeChange(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Types</option>
            {activityTypes.map(type => (
              <option key={type.id} value={type.name}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="child" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Child
          </label>
          <select
            id="child"
            value={selectedChild}
            onChange={(e) => onChildChange(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Children</option>
            {childrenCategories.map(child => (
              <option key={child.id} value={child.id}>
                {child.name}
              </option>
            ))}
          </select>
        </div>

        {(selectedChild === 'n/a' || childrenCategories.length === 0) && (
          <div>
            <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Age
            </label>
            <input
              type="text"
              value={age}
              onChange={(e) => onAgeChange(e.target.value)}
              placeholder="Enter age"
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>
        )}

        <div>
          <label htmlFor="duration" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Duration (minutes)
          </label>
          <input
            type="number"
            id="duration"
            value={duration}
            onChange={(e) => onDurationChange(e.target.value)}
            min="15"
            max="240"
            step="15"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="condition" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Environment
          </label>
          <select
            id="condition"
            value={condition}
            onChange={(e) => onConditionChange(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            {CONDITIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Location
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="text"
              id="location"
              value={location}
              onChange={(e) => onLocationChange(e.target.value)}
              placeholder="Enter a location"
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 pr-10 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              required
            />
            <button
              type="button"
              onClick={onCurrentLocation}
              className="absolute inset-y-0 right-0 px-3 flex items-center"
            >
              <MapPinIcon className="h-5 w-5 text-gray-400 hover:text-blue-500" />
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="distance" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Distance
          </label>
          <select
            id="distance"
            value={distance}
            onChange={(e) => onDistanceChange(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          >
            {DISTANCE_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="maxCost" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Max Cost
          </label>
          <input
            type="number"
            id="maxCost"
            value={maxCost}
            onChange={(e) => onMaxCostChange(e.target.value)}
            min="0"
            max="1000"
            step="10"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="considerations" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Special Considerations
          </label>
          <textarea
            id="considerations"
            value={considerations}
            onChange={(e) => onConsiderationsChange(e.target.value)}
            placeholder="Any special needs, preferences, or requirements?"
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={handleSearch}
          disabled={isLoading}
          className="flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? 'Searching...' : 'Find Activities'}
        </button>

        {showClearButton && (
          <button
            onClick={onClear}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Clear Results
          </button>
        )}
      </div>
    </div>
  );
};

export default FindActivitiesForm;